import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';

import {CreateAiContentItemRequest} from 'common/src/modules/aiContent/aiContentItem.types';
import createMultipleAiContentItems from 'common/src/modules/aiContent/store/aiContentProject/thunks/createMultipleAiContentItems';
import {AiContentProjectAgentToolName} from '../aiContentProjectAgent.enums';

const inputSchema = z.object({
  items: z
    .array(
      z.object({
        name: z.string().describe('The name of the item to create'),
        inputValues: z
          .record(z.unknown())
          .optional()
          .nullable()
          .describe('Key-value pairs of input column values for this item'),
      }),
    )
    .describe('Array of items to create with their input values'),
});

export default function createItemsTool(store: CLStore) {
  async function execute({
    input: {items},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const project = state.aiContentProjectPage?.project;

    if (!project?.id) {
      return toolResult('Error: No project is currently loaded. Cannot create items.');
    }

    if (!items || items.length === 0) {
      return toolResult(
        'Error: No items provided. Please specify at least one item to create.',
      );
    }

    const projectId = project.id;

    const itemRequests: CreateAiContentItemRequest[] = items.map((item) => ({
      name: item.name,
      projectId,
      inputValues: item.inputValues ?? {},
    }));

    try {
      await store
        .dispatch(
          createMultipleAiContentItems({
            projectId,
            request: {items: itemRequests},
          }),
        )
        .unwrap();

      const itemNames = items.map((item) => item.name).join(', ');

      return toolResult(
        `Successfully created ${items.length} item${items.length > 1 ? 's' : ''}: ${itemNames}`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      return toolResult(`Error creating items: ${errorMessage}`);
    }
  }

  return tool({
    displayName: 'Creating items',
    name: AiContentProjectAgentToolName.CreateItems,
    description:
      'Creates one or more AI content items with specified input values. Each item represents a variation that will have prompts and assets generated. Use this tool even when creating a single item.',
    execute,
    inputSchema,
  });
}
